import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Package, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { SECTION_ORDER, SECTION_LABELS_DE } from '../config/sectionLabels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMB = (b) => `${Math.round(b / (1024 * 1024))} MB`;
const fmtTime = (iso) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/**
 * AppRebuildCard — Admin-Werkzeug: paketiert die Offline-Desktop-Apps
 * (Windows .exe / Mac .zip) neu, damit Textänderungen aus dem Editor
 * in die installierbaren Apps übernommen werden. Nur sichtbar, wenn
 * die Build-Umgebung verfügbar ist (Preview).
 *
 * 08/2026: Erweitert um Slide-Auswahl — Admin kann pro-Build wählen
 * welche Folien in die Offline-App aufgenommen werden. Default =
 * aktuelle Admin-Visibility (kann für einen Build überschrieben werden,
 * ohne die Live-Präsentation zu beeinflussen).
 */
export default function AppRebuildCard() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [showSlides, setShowSlides] = useState(false);
  const [buildVis, setBuildVis] = useState({});
  const timerRef = useRef(null);

  // Initialise the build-time visibility once from the current admin config.
  useEffect(() => {
    if (!user || !user.section_visibility) return;
    const v = {};
    SECTION_ORDER.forEach((k) => {
      v[k] = user.section_visibility[k] !== false;
    });
    setBuildVis(v);
  }, [user]);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/rebuild-apps/status`);
      setState(data);
      return data;
    } catch (_) {
      setState({ available: false });
      return null;
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (state?.status === 'running') {
      timerRef.current = setInterval(fetchStatus, 5000);
    }
    return () => clearInterval(timerRef.current);
  }, [state?.status]);

  const visibleCount = useMemo(
    () => Object.values(buildVis).filter(Boolean).length,
    [buildVis],
  );

  if (!state || !state.available) return null;

  const running = state.status === 'running';

  const toggleSlide = (key) => setBuildVis((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleAll = (val) => {
    const v = {};
    SECTION_ORDER.forEach((k) => { v[k] = val; });
    setBuildVis(v);
  };

  const start = async () => {
    if (running) return;
    if (!window.confirm(`Offline-Apps mit ${visibleCount} Folien jetzt neu paketieren? Dauert ca. 15 Minuten.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/rebuild-apps`, {
        section_visibility: buildVis,
      });
      setState({ ...state, ...data, status: data.status === 'started' ? 'running' : data.status });
    } catch (_) { /* Status bleibt */ }
  };

  return (
    <div className="mt-4 p-3 rounded border border-white/10 bg-white/[0.03]" data-testid="app-rebuild-card">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1.5">
        <Package size={12} /> Offline-Apps
      </div>

      {/* Slide-Auswahl (collapsible) */}
      <button
        type="button"
        onClick={() => setShowSlides((s) => !s)}
        data-testid="app-rebuild-slides-toggle"
        className="w-full text-[11px] text-white/60 hover:text-white/90 flex items-center gap-1.5 mb-2"
      >
        {showSlides ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Folien-Auswahl ({visibleCount} von {SECTION_ORDER.length} aktiv)
      </button>
      {showSlides && (
        <div className="mb-3 p-2 rounded bg-black/30 border border-white/5">
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={() => toggleAll(true)}
              data-testid="app-rebuild-select-all"
              className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border border-white/15 text-white/70 hover:border-white/40"
            >Alle an</button>
            <button
              type="button"
              onClick={() => toggleAll(false)}
              data-testid="app-rebuild-select-none"
              className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border border-white/15 text-white/70 hover:border-white/40"
            >Alle aus</button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
            {SECTION_ORDER.map((k) => (
              <label
                key={k}
                data-testid={`app-rebuild-slide-${k}`}
                className="flex items-center gap-2 text-[11px] text-white/75 cursor-pointer hover:text-white"
              >
                <input
                  type="checkbox"
                  checked={!!buildVis[k]}
                  onChange={() => toggleSlide(k)}
                  className="cursor-pointer"
                />
                <span>{SECTION_LABELS_DE[k] || k}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={start}
        disabled={running}
        data-testid="app-rebuild-button"
        className={`w-full text-xs uppercase tracking-[0.12em] px-3 py-2 rounded border transition-colors ${
          running
            ? 'border-white/10 text-white/35 cursor-not-allowed'
            : 'border-white/25 text-white/85 hover:border-white/60'
        }`}
      >
        {running ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 size={13} className="animate-spin" /> Paketiert…
          </span>
        ) : `App bauen (${visibleCount} Folien)`}
      </button>

      {running && (
        <div className="mt-2 text-[11px] text-amber-200/80" data-testid="app-rebuild-step">
          {state.step || 'Startet…'} — ca. 15 Min., Seite kann offen bleiben
        </div>
      )}
      {state.status === 'done' && (
        <div className="mt-2 text-[11px] text-emerald-300/90 flex items-center gap-1" data-testid="app-rebuild-done">
          <CheckCircle2 size={12} /> Fertig — neue Apps im Dashboard downloadbar
        </div>
      )}
      {state.status === 'error' && (
        <div className="mt-2 text-[11px] text-red-300/90 flex items-start gap-1" data-testid="app-rebuild-error">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" /> Fehlgeschlagen — Log: {String(state.error || '').slice(-160)}
        </div>
      )}

      {(state.files || []).length > 0 && (
        <div className="mt-2 space-y-0.5">
          {state.files.map((f) => (
            <div key={f.key} className="text-[10px] text-white/40" data-testid={`app-rebuild-file-${f.key}`}>
              {f.key === 'windows' ? 'Windows' : 'Mac'} · {fmtMB(f.size_bytes)} · Stand {fmtTime(f.modified_at)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
